// tire-assignment.component.ts
import { Component, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface Tire {
  id: string;
  name: string;
  type: 'new' | 'old';
}

interface Group {
  name: string;
  positions: string[];
}

@Component({
  selector: 'app-tire-assignment',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tyre-position-drag-drop.component.html',
  styleUrls: ['./tyre-position-drag-drop.component.css']
})
export class TyrePositionDragDropComponent implements OnInit {
  // All tyres (fixed pool)
  allTires = signal<Tire[]>([
    { id: 'nt1', name: 'New Tyre 1', type: 'new' },
    { id: 'nt2', name: 'New Tyre 2', type: 'new' },
    { id: 'nt3', name: 'New Tyre 3', type: 'new' },
    { id: 'nt4', name: 'New Tyre 4', type: 'new' },
    { id: 'nt5', name: 'New Tyre 5', type: 'new' },
    { id: 'ot1', name: 'Old Tyre 1', type: 'old' },
    { id: 'ot2', name: 'Old Tyre 2', type: 'old' },
    { id: 'ot3', name: 'Old Tyre 3', type: 'old' },
    { id: 'ot4', name: 'Old Tyre 4', type: 'old' },
    { id: 'ot5', name: 'Old Tyre 5', type: 'old' },
  ]);

  // Current assignment: positionId → tireId
  assignment = signal<Record<string, string>>({});

  // Selected truck config
  selectedConfig = signal<'4x2' | '6x4' | '8x4' | '10x2'>('8x4');

  // Truck configurations (dynamic)
  private truckConfigs: Record<string, Group[]> = {
    '4x2': [
      { name: 'STEER', positions: ['F-L1', 'F-R1'] },
      { name: 'DRIVE1', positions: ['R-L1', 'R-R1'] }
    ],
    '6x4': [  
      { name: 'STEER', positions: ['F-L1', 'F-R1'] },
      { name: 'DRIVE1', positions: ['R-L1', 'R-L2', 'R-R1', 'R-R2'] }
    ],
    '8x4': [
      { name: 'STEER', positions: ['F-L1', 'F-R1'] },
      { name: 'DRIVE1', positions: ['R-L1', 'R-L2', 'R-R1', 'R-R2'] },
      { name: 'DRIVE2', positions: ['R-L3', 'R-L4', 'R-R3', 'R-R4'] },
      { name: 'SPARE', positions: ['SP-1', 'SP-2'] }
    ],
    '10x2': [
      { name: 'STEER', positions: ['F-L1', 'F-R1'] },
      { name: 'DRIVE1', positions: ['R-L1', 'R-L2', 'R-R1', 'R-R2'] },
      { name: 'DRIVE2', positions: ['R-L3', 'R-L4', 'R-R3', 'R-R4'] },
      { name: 'DRIVE3', positions: ['R-L5', 'R-R5'] }
    ]
  };

  groups = computed(() => this.truckConfigs[this.selectedConfig()]);

  ngOnInit() {
    // Optional: load from localStorage or backend on init
    console.log('🚛 Tire Assignment Component Loaded - Drag & Drop Ready');
  }

  // Available new/old tyres (computed)
  availableNewTires = computed(() => {
    const assignedIds = new Set(Object.values(this.assignment()));
    return this.allTires().filter(t => t.type === 'new' && !assignedIds.has(t.id));
  });

  availableOldTires = computed(() => {
    const assignedIds = new Set(Object.values(this.assignment()));
    return this.allTires().filter(t => t.type === 'old' && !assignedIds.has(t.id));
  });

  // Get tire by ID
  getTire(id: string): Tire | undefined {
    return this.allTires().find(t => t.id === id);
  }

  // Get assigned tire for a position
  getAssignedTire(posId: string): Tire | undefined {
    const tireId = this.assignment()[posId];
    return tireId ? this.getTire(tireId) : undefined;
  }

  // Drag Start
  onDragStart(event: DragEvent, tireId: string, fromPosition?: string) {
    const data = { tireId, fromPosition: fromPosition || null };
    event.dataTransfer?.setData('application/json', JSON.stringify(data));
    // Optional visual feedback
    const target = event.currentTarget as HTMLElement;
    target.classList.add('dragging');
  }

  // Drag Over (allow drop)
  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  // Highlight on enter
  onDragEnter(event: DragEvent) {
    const target = event.currentTarget as HTMLElement;
    if (target.classList.contains('position-box') || target.classList.contains('tyre-list-container')) {
      target.classList.add('drag-over');
    }
  }

  // Remove highlight
  onDragLeave(event: DragEvent) {
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  }

  // Main Drop Handler (handles ALL cases)
  onDrop(event: DragEvent, targetPosition: string | null) {
    event.preventDefault();
    const targetEl = event.currentTarget as HTMLElement;
    targetEl.classList.remove('drag-over');

    const jsonData = event.dataTransfer?.getData('application/json');
    if (!jsonData) return;

    const { tireId, fromPosition } = JSON.parse(jsonData);
    if (!tireId) return;

    let currentAssignment = { ...this.assignment() };

    // Step 1: Remove tire from previous location
    if (fromPosition && currentAssignment[fromPosition] === tireId) {
      delete currentAssignment[fromPosition];
    }

    // Step 2: Place tire in new location
    if (targetPosition) {
      // Drop on truck position → if position was occupied, old tire automatically becomes available
      currentAssignment[targetPosition] = tireId;
    } 
    // else: drop on right panel → just unassign (already handled in Step 1)

    // Update signal (reactive)
    this.assignment.set(currentAssignment);

    // Animation feedback
    console.log(`✅ Tyre moved: ${tireId} → ${targetPosition || 'Available List'}`);
  }

  // Change truck config (dynamic positions)
  onConfigChange() {
    // Clear assignments that no longer exist in new config
    const currentGroups = this.truckConfigs[this.selectedConfig()];
    const validPositions = new Set(currentGroups.flatMap(g => g.positions));
    
    let cleaned = { ...this.assignment() };
    Object.keys(cleaned).forEach(pos => {
      if (!validPositions.has(pos)) delete cleaned[pos];
    });
    this.assignment.set(cleaned);
  }

  // Save to backend (payload example)
  saveToBackend() {
    const payload = {
      truckType: this.selectedConfig(),
      timestamp: new Date().toISOString(),
      assignments: this.assignment(),           // positionId: tireId
      availableNew: this.availableNewTires().map(t => t.id),
      availableOld: this.availableOldTires().map(t => t.id)
    };

    console.log('📤 Payload to Backend:', payload);
    // alert('✅ Configuration Saved! Check console for payload.\n\nYou can now send this to your API.');
    // Example: this.http.post('/api/tyre-config', payload).subscribe();
  }

  // Reset everything
  resetAll() {
    if (confirm('Reset all tyre positions?')) {
      this.assignment.set({});
    }
  }
}